"use client";


import React, { useState } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';
import FacilityRequestModal from '@/components/common/FacilityRequestModal';
import ClinicianApplicationModal from '@/components/common/ClinicianApplicationModal';

export default function AboutPage() {
  const [facilityModalOpen, setFacilityModalOpen] = useState(false);
  const [clinicianModalOpen, setClinicianModalOpen] = useState(false);

  const stats = [
    { id: 'stat_1', value: '2,800+', label: 'Placements in 2025' },
    { id: 'stat_2', value: '450+', label: 'Facilities Served' },
    { id: 'stat_3', value: '18 days', label: 'Average Time-to-Hire' },
    { id: 'stat_4', value: '<5%', label: 'Replacement Rate' },
  ];

  const values = [
    {
      id: 'value_1',
      icon: 'ChatBubbleLeftRightIcon',
      title: 'Straightforward communication',
      description: 'We say what we mean. No runaround, no jargon, no games. If we don\'t know something, we say so.',
    },
    {
      id: 'value_2',
      icon: 'ShieldCheckIcon',
      title: 'Quality over speed',
      description: 'We\'re fast, but not reckless. Every candidate is thoroughly screened. Every facility gets honest feedback.',
    },
    {
      id: 'value_3',
      icon: 'HandRaisedIcon',
      title: 'Long-term relationships',
      description: 'We don\'t disappear after placement. We stay involved through start date and check in at 30, 60, and 90 days.',
    },
    {
      id: 'value_4',
      icon: 'UserGroupIcon',
      title: 'Respect for both sides',
      description: 'Facilities need qualified staff. Clinicians deserve good opportunities. We serve both with equal commitment.',
    },
  ];

  return (
    <>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-accent/30 to-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
              About INF Medical
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed">
              We're a permanent healthcare recruiting firm that does things differently.
              <br />
              No contracts. No travel. Just full-time placements done right.
            </p>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-6">
                  Our Story
                </h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed text-sm sm:text-base">
                  <p>
                    INF Medical was founded in 2018 by two former hospital administrators who were frustrated with the permanent hiring process. Every recruiting firm promised fast results, but most delivered unqualified candidates or disappeared after placement.
                  </p>
                  <p>
                    We built INF Medical around a simple principle: do what we say we'll do. If we commit to 48-hour turnaround, we deliver in 48 hours. If we screen for culture fit, we actually assess culture fit. If we offer a 90-day guarantee, we honor it without hassle.
                  </p>
                  <p>
                    Seven years later, we've placed over 12,000 healthcare professionals in permanent roles. We're not the biggest recruiting firm, but we're the most straightforward.
                  </p>
                </div>
              </div>
              <div className="bg-accent/50 p-8 rounded-2xl border border-border">
                <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-6">Our Mission</h3>
                <p className="text-base sm:text-lg text-foreground leading-relaxed">
                  To connect healthcare facilities with qualified permanent staff through transparent communication, rigorous screening, and genuine commitment to long-term fit.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 md:py-24 bg-gradient-medical">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-white">By the Numbers</h2>
              <p className="text-base sm:text-lg md:text-xl text-white">
                Results that speak for themselves.
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat) => (
                <div key={stat.id} className="text-center">
                  <div className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2 text-white">{stat.value}</div>
                  <div className="text-sm sm:text-base text-white">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Approach */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                What Makes Us Different
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
                Four principles that guide everything we do.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {values.map((value) => (
                <div
                  key={value.id}
                  className="bg-accent/50 p-8 rounded-xl border border-border hover:shadow-lg transition-all"
                >
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                    <Icon name={value.icon as any} size={28} className="text-primary" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-3">
                    {value.title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-accent/30">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-6">
              Ready to work together?
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground mb-8">
              Whether you're a facility looking to hire or a clinician seeking your next role, we're here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setFacilityModalOpen(true)}
                className="px-8 py-4 bg-primary text-white rounded-lg text-base sm:text-lg font-semibold hover:bg-primary/90 transition-all shadow-lg w-full sm:w-auto"
              >
                Request Candidates
              </button>
              <button
                onClick={() => setClinicianModalOpen(true)}
                className="px-8 py-4 bg-white text-foreground border border-border rounded-lg text-base sm:text-lg font-semibold hover:bg-accent transition-all w-full sm:w-auto"
              >
                Apply for Jobs
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FacilityRequestModal isOpen={facilityModalOpen} onClose={() => setFacilityModalOpen(false)} />
      <ClinicianApplicationModal isOpen={clinicianModalOpen} onClose={() => setClinicianModalOpen(false)} />
    </>
  );
}